"use client";

/**
 * Browser-side product analytics.
 *
 * The wrapper auto-attaches base properties (org via the sticky
 * `organization` group, survey via per-event `survey` group,
 * `client_session_id` when session recording is active, and
 * `viewer_kind: "workspace"`). Callers pass only event-specific fields
 * inside `properties`.
 *
 * Always pass `surveyId` as a first-class field (not via `properties`);
 * it populates both the `survey_id` property and the per-event `survey`
 * PostHog group.
 */
import { getEventSurface } from "./baseProperties";
import type { EventName, EventPropertyMap } from "./events";
import type { ViewerKind } from "./types";
import { fireAndForget } from "@/lib/vercelUtils";
import { logger } from "@/utils/logging/browser";
import {
  applyBootstrappedFlags,
  invalidateBootstrappedFlags,
} from "@/utils/posthog/bootstrapFlags";
import {
  getRenderedTheme,
  POSTHOG_BASE_PROPERTIES,
} from "@/utils/posthog/browserConfig";
import type {
  DistributiveOmit,
  OrganizationId,
  SurveyId,
  UserId,
} from "@/utils/types";
import posthog from "posthog-js";

type IdentifyProperties = Record<string, unknown>;

// "beacon" sends instantly via navigator.sendBeacon: PostHog's own unload flush runs before our pagehide handlers, so a queued event is lost.
export type TrackDelivery = "beacon";

type TrackArgs<E extends EventName> = {
  event: E;
  surveyId?: SurveyId | null;
  properties?: EventPropertyMap[E];
  /**
   * Override the default `viewer_kind: "authenticated"`. Required when the
   * client wrapper is called from a surface that anonymous users can also
   * reach (e.g. the recruitment dashboard share page).
   */
  viewerKind?: ViewerKind;
  delivery?: TrackDelivery;
};

const getClientSessionId = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  if (!posthog.sessionRecordingStarted()) return undefined;
  return posthog.get_session_id();
};

// Untyped so `trackClientWithContext` can merge properties without a cast;
// the public methods type-check against `EventPropertyMap`.
const capture = ({
  event,
  surveyId,
  properties,
  viewerKind,
  delivery,
}: {
  event: EventName;
  surveyId?: SurveyId | null;
  properties?: object;
  viewerKind?: ViewerKind;
  delivery?: TrackDelivery;
}): void => {
  if (typeof window === "undefined") return;
  try {
    const { survey: _staleSurvey, ...stickyGroups } = posthog.getGroups();
    const clientSessionId = getClientSessionId();
    // Survey is per-event only; org is sticky from `identifyOrganization`.
    const eventProperties = {
      surface: getEventSurface(event),
      viewer_kind: viewerKind ?? "authenticated",
      ...(clientSessionId ? { client_session_id: clientSessionId } : {}),
      ...properties,
      ...(surveyId
        ? {
            survey_id: surveyId,
            $groups: { ...stickyGroups, survey: surveyId },
          }
        : { $groups: stickyGroups }),
    };
    if (delivery === "beacon") {
      posthog.capture(event, eventProperties, {
        send_instantly: true,
        transport: "sendBeacon",
      });
    } else {
      posthog.capture(event, eventProperties);
    }
  } catch (error) {
    console.error("[productAnalytics.trackClient] failed:", error);
  }
};

// Events whose schema declares every context property.
export type ContextEventName<Ctx extends object> = {
  [K in EventName]: EventPropertyMap[K] extends Ctx ? K : never;
}[EventName];

export const productAnalytics = {
  trackClient: <E extends EventName>(args: TrackArgs<E>): void => capture(args),

  // Backs `createAnalyticsContext`; use that instead of calling this directly.
  trackClientWithContext: <
    Ctx extends object,
    E extends ContextEventName<Ctx>,
  >({
    contextProperties,
    properties,
    ...args
  }: {
    event: E;
    surveyId: SurveyId | null;
    viewerKind?: ViewerKind;
    contextProperties: Ctx;
    properties: DistributiveOmit<EventPropertyMap[E], keyof Ctx>;
    delivery?: TrackDelivery;
  }): void =>
    capture({ ...args, properties: { ...contextProperties, ...properties } }),

  identifyUser: ({
    userId,
    email,
    name,
    isAdminViewer,
    properties,
  }: {
    userId: UserId;
    email?: string;
    name?: string | null;
    isAdminViewer: boolean;
    properties?: IdentifyProperties;
  }): void => {
    if (typeof window === "undefined") return;
    try {
      posthog.identify(userId, {
        ...(email ? { email, $email: email } : {}),
        ...(name ? { name } : {}),
        is_admin_viewer: isAdminViewer,
        ...properties,
      });
      // Super-property so every subsequent capture (including $pageview and
      // autocapture) carries `is_admin_viewer`. Cleared by `posthog.reset()`
      // on logout.
      posthog.register({ is_admin_viewer: isAdminViewer });
      if (email) {
        const domain = email.split("@")[1];
        if (domain) posthog.register({ email_domain: domain });
      }
      fireAndForget(applyBootstrappedFlags());
    } catch (error) {
      console.error("[productAnalytics.identifyUser] failed:", error);
    }
  },

  identifyOrganization: ({
    orgId,
    name,
    properties,
  }: {
    orgId: OrganizationId;
    name?: string | null;
    properties?: IdentifyProperties;
  }): void => {
    if (typeof window === "undefined") return;
    try {
      posthog.group("organization", orgId, {
        ...(name ? { name } : {}),
        ...properties,
      });
    } catch (error) {
      console.error("[productAnalytics.identifyOrganization] failed:", error);
    }
  },

  reset: (): void => {
    if (typeof window === "undefined") return;
    try {
      posthog.reset();
      invalidateBootstrappedFlags();
      posthog.featureFlags.updateFlags({}, {});
      fireAndForget(applyBootstrappedFlags());
      posthog.register(POSTHOG_BASE_PROPERTIES);
      // `reset` clears super-properties, and the observer in AnalyticsProvider
      // only fires on a `.dark` class change, which a light-mode user or a
      // failed sign-out never produces.
      posthog.register({ theme: getRenderedTheme() });
    } catch (error) {
      console.error("[productAnalytics.reset] failed:", error);
    }
  },

  // Session-scoped so a closed tab can't leak the property onto later pages.
  registerSessionProperties: (properties: Record<string, string>): void => {
    if (typeof window === "undefined") return;
    try {
      posthog.register_for_session(properties);
    } catch (error) {
      logger.error(
        "[productAnalytics.registerSessionProperties] failed",
        error
      );
    }
  },

  unregisterSessionProperty: (property: string): void => {
    if (typeof window === "undefined") return;
    try {
      posthog.unregister_for_session(property);
    } catch (error) {
      logger.error(
        "[productAnalytics.unregisterSessionProperty] failed",
        error
      );
    }
  },

  /**
   * Returns the active PostHog session id when session recording is running.
   * Used to stitch product events to a recorded session in PostHog. Returns
   * `undefined` when recording is not active or when called server-side.
   */
  getRecordingSessionId: (): string | undefined => getClientSessionId(),
};

export type ProductAnalytics = typeof productAnalytics;
